<template>
  <transition name="modal-fade">
    <div v-if="isOpen" class="modal-overlay" @click.self="closeModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h2>
            <IconSvg name="map-solid-full" size="md" />
            {{ editMode ? "行きたいとこ編集" : "行きたいとこ追加" }}
          </h2>
          <button class="close-button" @click="closeModal" aria-label="閉じる">×</button>
        </div>

        <div v-if="errorMessage" class="error-message" role="alert" aria-live="assertive">{{ errorMessage }}</div>

        <form @submit.prevent="handleSubmit" class="modal-body">
          <!-- 場所名 -->
          <div class="form-group">
            <label for="placeNmv">場所名 <span class="required">*</span></label>
            <GooglePlaceInput
              input-id="placeNmv"
              v-model="formData.placeNmv"
              placeholder="東京タワーなど、場所を入力して選択"
              :maxlength="255"
              required
              @place-selected="handlePlaceSelected"
              @manual-input="clearSelectedPlace"
            />
          </div>

          <div class="form-group tag-form-group">
            <label for="wishlistTags">タグ</label>
            <div class="tag-input-shell" :class="{ 'has-error': tagError }">
              <div v-if="formData.tags.length" class="tag-chip-list" aria-label="設定済みタグ">
                <span v-for="tag in formData.tags" :key="tag" class="tag-chip">
                  {{ tag }}
                  <button type="button" :aria-label="`${tag}タグを削除`" @click="removeTag(tag)">×</button>
                </span>
              </div>
              <input
                id="wishlistTags"
                v-model="tagInput"
                type="text"
                maxlength="209"
                :disabled="formData.tags.length >= 10"
                :aria-describedby="tagError ? 'wishlistTagsHelp wishlistTagsError' : 'wishlistTagsHelp'"
                :aria-invalid="Boolean(tagError)"
                placeholder="例：1日目、京都（Enterまたはカンマで追加）"
                @keydown="handleTagKeydown"
                @compositionstart="isComposing = true"
                @compositionend="handleCompositionEnd"
                @blur="commitTag"
              />
            </div>
            <p id="wishlistTagsHelp" class="tag-help">自由に最大10個・各20文字まで</p>
            <p v-if="tagError" id="wishlistTagsError" class="field-error" role="alert">{{ tagError }}</p>
          </div>

          <!-- カテゴリ -->
          <div class="form-group">
            <label for="categoryNmv">カテゴリ</label>
            <select id="categoryNmv" v-model="formData.categoryNmv">
              <option value="">選択してください</option>
              <option value="観光">観光</option>
              <option value="グルメ">グルメ</option>
              <option value="ショッピング">ショッピング</option>
              <option value="宿泊">宿泊</option>
              <option value="アクティビティ">アクティビティ</option>
              <option value="その他">その他</option>
            </select>
          </div>

          <!-- 住所 -->
          <div class="form-group">
            <label for="addressNmv">住所・場所メモ</label>
            <input
              type="text"
              id="addressNmv"
              v-model="formData.addressNmv"
              placeholder="例: 京都府京都市東山区清水1丁目"
              maxlength="255"
            />
          </div>

          <!-- URL -->
          <div class="form-group">
            <label for="urlNmv">参考URL</label>
            <input
              type="url"
              id="urlNmv"
              v-model="formData.urlNmv"
              placeholder="https://..."
              maxlength="500"
            />
          </div>

          <div class="form-group">
            <label for="amount">金額（円）</label>
            <input
              type="text"
              id="amount"
              v-model="displayAmount"
              inputmode="numeric"
              placeholder="0"
              @focus="handleAmountFocus"
              @input="handleAmountInput"
              @blur="handleAmountBlur"
            />
          </div>

          <!-- メモ -->
          <div class="form-group">
            <label for="memoTxv">メモ・コメント</label>
            <textarea
              id="memoTxv"
              v-model="formData.memoTxv"
              placeholder="行きたい理由や注意事項など"
              rows="3"
              maxlength="1000"
            ></textarea>
          </div>

          <div class="modal-footer">
            <button
              type="button"
              class="cancel-button"
              @click="closeModal"
              :disabled="isSubmitting"
            >
              キャンセル
            </button>
            <button
              type="submit"
              class="submit-button"
              :disabled="isSubmitting"
            >
              {{ isSubmitting ? "処理中..." : editMode ? "更新" : "追加" }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </transition>
</template>

<script>
import { ref, watch } from "vue";
import { useWishlistStore } from "@/store/wishlist-store";
import IconSvg from "@/components/IconSvg.vue";
import GooglePlaceInput from "@/components/GooglePlaceInput.vue";
import { GOOGLE_MAPS_API_KEY } from "@/config/api.js";

export default {
  name: "CreateWishlistModal",
  components: { IconSvg, GooglePlaceInput },
  props: {
    isOpen: { type: Boolean, required: true },
    shioriId: { type: String, required: true },
    editMode: { type: Boolean, default: false },
    wishlistData: { type: Object, default: null },
  },
  emits: ["close", "create", "update"],
  setup(props, { emit }) {
    const wishlistStore = useWishlistStore();

    const formData = ref({
      placeNmv: "",
      categoryNmv: "",
      addressNmv: "",
      urlNmv: "",
      memoTxv: "",
      latitude: null,
      longitude: null,
      amount: null,
      currency: "JPY",
      tags: [],
    });

    const errorMessage = ref("");
    const isSubmitting = ref(false);
    const displayAmount = ref("");
    const tagInput = ref("");
    const tagError = ref("");
    const isComposing = ref(false);

    const commitTag = () => {
      if (isComposing.value) return;
      const candidates = tagInput.value
        .split(",")
        .map((tag) => tag.trim())
        .filter(Boolean);
      tagError.value = "";
      if (!candidates.length) {
        tagInput.value = "";
        return;
      }

      const rejectedTags = [];
      const errors = [];
      const describeError = (tag, message) =>
        candidates.length > 1 ? `「${tag}」${message}` : message;
      candidates.forEach((tag) => {
        if (tag.length > 20) {
          rejectedTags.push(tag);
          errors.push(describeError(tag, "タグは20文字以内で入力してください"));
        } else if (formData.value.tags.includes(tag)) {
          rejectedTags.push(tag);
          errors.push(describeError(tag, "同じタグは追加できません"));
        } else if (formData.value.tags.length >= 10) {
          rejectedTags.push(tag);
          errors.push(describeError(tag, "タグは10個まで追加できます"));
        } else {
          formData.value.tags.push(tag);
        }
      });
      tagInput.value = rejectedTags.join(", ");
      tagError.value = errors.join("。 ");
    };
    const handleTagEnter = () => {
      if (!isComposing.value) commitTag();
    };
    const handleTagKeydown = (event) => {
      if (event.key !== "Enter" && event.key !== ",") return;
      if (event.isComposing || isComposing.value) return;
      event.preventDefault();
      handleTagEnter();
    };
    const handleCompositionEnd = () => {
      isComposing.value = false;
    };
    const removeTag = (tag) => {
      formData.value.tags = formData.value.tags.filter((item) => item !== tag);
      tagError.value = "";
    };

    const handleAmountInput = (event) => {
      let value = event.target.value.replace(/[^0-9]/g, "");
      value = value.replace(/^0+(\d)/, "$1");
      displayAmount.value = value;
      formData.value.amount = value ? Number(value) : null;
    };

    const handleAmountFocus = () => {
      if (formData.value.amount !== null) {
        displayAmount.value = String(formData.value.amount);
      }
    };

    const handleAmountBlur = () => {
      displayAmount.value =
        formData.value.amount !== null
          ? Number(formData.value.amount).toLocaleString("ja-JP")
          : "";
    };

    const closeModal = () => {
      emit("close");
      setTimeout(resetForm, 300);
    };

    const resetForm = () => {
      formData.value = {
        placeNmv: "",
        categoryNmv: "",
        addressNmv: "",
        urlNmv: "",
        memoTxv: "",
        latitude: null,
        longitude: null,
        amount: null,
        currency: "JPY",
        tags: [],
      };
      tagInput.value = "";
      tagError.value = "";
      displayAmount.value = "";
      errorMessage.value = "";
    };

    const handlePlaceSelected = (place) => {
      formData.value.placeNmv = place.name || formData.value.placeNmv;
      formData.value.addressNmv = place.address || formData.value.addressNmv;
      formData.value.latitude = Number.isFinite(place.latitude)
        ? place.latitude
        : null;
      formData.value.longitude = Number.isFinite(place.longitude)
        ? place.longitude
        : null;
    };

    const clearSelectedPlace = () => {
      formData.value.latitude = null;
      formData.value.longitude = null;
    };

    const handleSubmit = async () => {
      commitTag();
      if (tagError.value) return;
      isSubmitting.value = true;
      errorMessage.value = "";
      try {
        if (
          GOOGLE_MAPS_API_KEY &&
          !formData.value.placeNmv.trim() &&
          (formData.value.latitude === null ||
            formData.value.latitude === undefined ||
            formData.value.longitude === null ||
            formData.value.longitude === undefined)
        ) {
          errorMessage.value = "場所名を入力してください";
          return;
        }
        if (props.editMode && props.wishlistData) {
          await wishlistStore.updateWishlist(
            props.shioriId,
            props.wishlistData.WISH_NO,
            {
              placeNmv: formData.value.placeNmv,
              categoryNmv: formData.value.categoryNmv || null,
              addressNmv: formData.value.addressNmv || null,
              latitude: formData.value.latitude ?? null,
              longitude: formData.value.longitude ?? null,
              urlNmv: formData.value.urlNmv || null,
              memoTxv: formData.value.memoTxv || null,
              amount: formData.value.amount ?? null,
              currency: "JPY",
              tags: [...formData.value.tags],
            },
          );
          emit("update");
        } else {
          await wishlistStore.createWishlist({
            shioriId: props.shioriId,
            placeNmv: formData.value.placeNmv,
            categoryNmv: formData.value.categoryNmv || null,
            addressNmv: formData.value.addressNmv || null,
            latitude: formData.value.latitude ?? null,
            longitude: formData.value.longitude ?? null,
            urlNmv: formData.value.urlNmv || null,
            memoTxv: formData.value.memoTxv || null,
            amount: formData.value.amount ?? null,
            currency: "JPY",
            tags: [...formData.value.tags],
          });
          emit("create");
        }
        closeModal();
      } catch (err) {
        errorMessage.value =
          err.response?.data?.detail ||
          (props.editMode ? "更新に失敗しました" : "追加に失敗しました");
      } finally {
        isSubmitting.value = false;
      }
    };

    watch(
      () => props.isOpen,
      (isOpen) => {
        if (isOpen) {
          if (props.editMode && props.wishlistData) {
            formData.value = {
              placeNmv: props.wishlistData.PLACE_NMV || "",
              categoryNmv: props.wishlistData.CATEGORY_NMV || "",
              addressNmv: props.wishlistData.ADDRESS_NMV || "",
              urlNmv: props.wishlistData.URL_NMV || "",
              memoTxv: props.wishlistData.MEMO_TXV || "",
              latitude: props.wishlistData.LATITUDE ?? null,
              longitude: props.wishlistData.LONGITUDE ?? null,
              amount:
                props.wishlistData.AMOUNT !== null &&
                props.wishlistData.AMOUNT !== undefined
                  ? Number(props.wishlistData.AMOUNT)
                  : null,
              currency: props.wishlistData.CURRENCY || "JPY",
              tags: Array.isArray(props.wishlistData.TAGS)
                ? [...props.wishlistData.TAGS]
                : [],
            };
            handleAmountBlur();
          } else {
            resetForm();
          }
        }
      },
    );

    return {
      formData,
      errorMessage,
      isSubmitting,
      closeModal,
      handleSubmit,
      handlePlaceSelected,
      clearSelectedPlace,
      displayAmount,
      handleAmountInput,
      handleAmountFocus,
      handleAmountBlur,
      tagInput,
      tagError,
      isComposing,
      commitTag,
      handleTagEnter,
      handleTagKeydown,
      handleCompositionEnd,
      removeTag,
    };
  },
};
</script>

<style scoped>
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s;
}
.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 520px;
  max-height: calc(100dvh - 1rem);
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.modal-header h2 {
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
}

.close-button {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #6b7280;
  cursor: pointer;
  line-height: 1;
}
.close-button:hover {
  color: #111827;
}

.modal-body {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.form-group label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
}

.form-group input,
.form-group select,
.form-group textarea {
  padding: 0.625rem 0.875rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.9375rem;
  color: #111827;
  background: white;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.tag-input-shell {
  padding: 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  background: #fff;
}
.tag-input-shell:focus-within { border-color: #52734d; box-shadow: 0 0 0 3px rgba(82, 115, 77, 0.14); }
.tag-input-shell.has-error { border-color: #dc2626; }
.tag-input-shell input { width: 100%; border: 0; padding: 0.375rem; box-shadow: none !important; }
.tag-chip-list { display: flex; flex-wrap: wrap; gap: 0.375rem; margin-bottom: 0.375rem; }
.tag-chip { display: inline-flex; align-items: center; max-width: 100%; gap: 0.25rem; padding: 0.25rem 0.5rem; border-radius: 999px; background: #e7f0df; color: #294525; font-size: 0.8125rem; overflow-wrap: anywhere; }
.tag-chip button { display: inline-grid; place-items: center; width: 44px; height: 44px; margin: -0.625rem -0.5rem -0.625rem 0; border: 0; background: transparent; color: inherit; cursor: pointer; font-size: 1rem; line-height: 1; }
.tag-help, .field-error { margin: 0; font-size: 0.75rem; }
.tag-help { color: #6b7280; }
.field-error { color: #b91c1c; }

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-group textarea {
  resize: vertical;
}

.required {
  color: #ef4444;
}

.error-message {
  margin: 0 1.5rem;
  padding: 0.75rem 1rem;
  background: #fee2e2;
  border: 1px solid #fecaca;
  color: #dc2626;
  border-radius: 8px;
  font-size: 0.875rem;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding-top: 0.5rem;
}

.cancel-button {
  padding: 0.625rem 1.25rem;
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.9375rem;
  color: #374151;
  cursor: pointer;
  transition: background 0.2s;
}
.cancel-button:hover:not(:disabled) {
  background: #e5e7eb;
}

.submit-button {
  padding: 0.625rem 1.5rem;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.9375rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}
.submit-button:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
}
.submit-button:disabled,
.cancel-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
